/**
 * CheckoutIframeModal — modal full-screen com iframe do checkout hospedado.
 *
 * Estratégia: SafraPay HML não retorna X-Frame-Options nem CSP
 * frame-ancestors (validado 2026-05-31 via Computer Use no portal
 * portal-hml.safrapay.com.br) — então embedamos o smartCheckout diretamente
 * em <iframe>. Pessoa nunca sai do widget, UX fica de app nativo.
 *
 * Fallback: se o iframe.onload não disparar em 5s OU o user prefere janela
 * dedicada, botão "Abrir em nova janela" usa window.open. Útil tb se algum
 * dia o provider mudar de comportamento e bloquear iframe em prod.
 *
 * Polling: GET /v1/public/payments/{order_id}/status @ 2.5s, timeout
 * duro 15min (alinhado com expires_at default da PaymentOrder).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ApiCallError } from '../api/types';
import type { PublicPaymentStatus } from '../api/types';
import type { CalculatorClient } from '../api/client';

interface Props {
  client: CalculatorClient;
  sessionUrl: string;
  orderId: string;
  /** Chamado quando o backend confirma pagamento bem-sucedido (webhook → paid). */
  onPaid: () => void;
  /** Chamado quando o pagamento falha/expira/cancela. */
  onFailed: (reason: string) => void;
  /** Chamado quando a pessoa fecha o modal manualmente sem completar. */
  onCancel: () => void;
}

const POLL_INTERVAL_MS = 2500;
const POLL_MAX_DURATION_MS = 15 * 60 * 1000;
const IFRAME_LOAD_TIMEOUT_MS = 5000;

export function CheckoutIframeModal({
  client,
  sessionUrl,
  orderId,
  onPaid,
  onFailed,
  onCancel,
}: Props) {
  const [status, setStatus] = useState<PublicPaymentStatus['status']>('pending');
  const [pollError, setPollError] = useState<string | undefined>();
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [showFallback, setShowFallback] = useState(false);
  const startedAtRef = useRef<number>(Date.now());
  const popupRef = useRef<Window | null>(null);

  // Fallback: abre janela popup se o user clicar "Abrir em nova janela"
  // ou se o iframe não carregar em IFRAME_LOAD_TIMEOUT_MS.
  const openPaymentWindow = useCallback(() => {
    if (popupRef.current && !popupRef.current.closed) {
      popupRef.current.focus();
      return;
    }
    const w = 480;
    const h = 720;
    const left = Math.max(0, (window.innerWidth - w) / 2);
    const top = Math.max(0, (window.innerHeight - h) / 2);
    popupRef.current = window.open(
      sessionUrl,
      'oma-checkout',
      `popup=yes,width=${w},height=${h},left=${left},top=${top}`,
    );
  }, [sessionUrl]);

  // Se o iframe não carregar rápido, oferece o fallback de abrir em nova janela.
  useEffect(() => {
    const t = setTimeout(() => {
      if (!iframeLoaded) setShowFallback(true);
    }, IFRAME_LOAD_TIMEOUT_MS);
    return () => clearTimeout(t);
  }, [iframeLoaded]);

  // Polling do status.
  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    const tick = async () => {
      if (cancelled) return;
      if (Date.now() - startedAtRef.current > POLL_MAX_DURATION_MS) {
        onFailed('timeout');
        return;
      }
      try {
        const res = await client.paymentStatus(orderId, controller.signal);
        if (cancelled) return;
        setStatus(res.status);
        if (res.status === 'paid') {
          onPaid();
          return;
        }
        if (res.status === 'failed' || res.status === 'expired' || res.status === 'cancelled') {
          onFailed(res.status);
          return;
        }
        setTimeout(tick, POLL_INTERVAL_MS);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiCallError && err.status === 404) {
          onFailed('order_not_found');
          return;
        }
        setPollError(err instanceof Error ? err.message : 'network');
        setTimeout(tick, POLL_INTERVAL_MS);
      }
    };

    const firstTick = setTimeout(tick, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearTimeout(firstTick);
      controller.abort();
      // Fecha popup se modal desmontar.
      if (popupRef.current && !popupRef.current.closed) {
        popupRef.current.close();
      }
    };
  }, [client, orderId, onFailed, onPaid]);

  // Lock body scroll enquanto modal aberta.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  // Portal pro <body> — escapa de containing block do WidgetShell.
  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Pagamento"
      style={{
        position: 'fixed',
        inset: 0,
        width: '100vw',
        height: '100vh',
        zIndex: 2147483647,
      }}
      className="flex flex-col bg-ink/80 sm:p-6"
    >
      {/* Card do modal — full-screen no mobile, ~600x800 no desktop. */}
      <div className="flex flex-col bg-white sm:rounded-lg shadow-2xl w-full h-full sm:max-w-[640px] sm:max-h-[860px] sm:mx-auto sm:my-auto overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-creme-300 shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            <span
              className={`size-2.5 rounded-full shrink-0 ${
                status === 'paid' ? 'bg-floresta' : 'bg-cobre animate-pulse'
              }`}
              aria-hidden
            />
            <p className="font-display text-sm sm:text-base text-floresta truncate">
              {status === 'paid' ? 'Pagamento confirmado' : 'Pagamento'}
            </p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            className="size-8 rounded-full hover:bg-creme-200 flex items-center justify-center text-tabaco hover:text-ink transition-colors shrink-0"
            aria-label="Fechar"
          >
            ✕
          </button>
        </div>

        {/* Body */}
        {status === 'paid' ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-3 px-6 py-10">
            <div className="size-16 rounded-full bg-floresta/15 flex items-center justify-center text-floresta text-3xl">
              ✓
            </div>
            <p className="text-lg font-display text-floresta text-center">
              Pagamento confirmado
            </p>
            <p className="text-xs text-tabaco text-center">
              Preparando seu certificado…
            </p>
          </div>
        ) : (
          <div className="flex-1 flex flex-col relative bg-creme-100">
            {/* Iframe ocupa todo o espaço disponível. */}
            <iframe
              src={sessionUrl}
              title="Checkout"
              className="absolute inset-0 w-full h-full border-0"
              allow="payment *; clipboard-write"
              referrerPolicy="no-referrer-when-downgrade"
              onLoad={() => setIframeLoaded(true)}
            />

            {/* Loading overlay até o iframe carregar. */}
            {!iframeLoaded && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-creme-100">
                <div className="size-10 rounded-full border-2 border-cobre/30 border-t-cobre animate-spin" />
                <p className="text-sm text-tabaco">Carregando página de pagamento…</p>
              </div>
            )}

            {/* Fallback: oferece abrir em nova janela se demorar pra carregar. */}
            {showFallback && !iframeLoaded && (
              <div className="absolute inset-x-0 bottom-0 bg-white border-t border-creme-300 p-4 flex flex-col gap-2 shadow-lg">
                <p className="text-xs text-tabaco text-center">
                  Está demorando pra carregar? Você pode abrir em uma janela
                  separada.
                </p>
                <button
                  type="button"
                  onClick={openPaymentWindow}
                  className="rounded-md bg-floresta text-cream text-sm font-medium py-2.5 hover:bg-floresta/90 transition-colors"
                >
                  Abrir em nova janela
                </button>
              </div>
            )}
          </div>
        )}

        {/* Footer com status discreto. */}
        {status !== 'paid' && (
          <div className="px-4 py-2 border-t border-creme-300 text-center shrink-0">
            <p className="text-2xs text-tabaco">
              {pollError
                ? '(reconectando ao servidor…)'
                : 'Vamos atualizar esta tela quando o pagamento for confirmado.'}
            </p>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
